import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  useMemo
} from 'react';
import { useHistory } from 'react-router';
import Cookies from 'universal-cookie';
import queryString from 'query-string';
import jwt from 'jsonwebtoken';
import useTranslations from '../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import fetchAPI from '../../utils/fetchAPI';
import log from '../../utils/log';
import LoginPageView from '../view/LoginPageView';
import getTenant from '../../utils/getTenant';
import { VINCI_SUPPORT_EMAIL } from '../../utils/Constants';
import zxcvbn from 'zxcvbn';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import { APP_LICENSE } from '../../GetGlobalProps';
import getLicense, { LICENSE_TYPE } from '../../utils/getLicense';
import FormsService from '../../services/FormsService';
import { isEmpty } from '../../utils/isObjecEmpty';
import { setValue as setLSValue } from '../../utils/LocalStorageUtils';
import { responsiveFontSizes } from '@material-ui/core';

const cookies = new Cookies();

export const STEPS = {
  EMAIL: 0,
  PASSWORD: 1,
  ACTIVATE_ACCOUNT: 2,
  SET_PASSWORD: 3
};

const MAX_NUM_OF_FAILED_LOGIN_ATTEMPTES = 3; // used to determaine when to show a dialog for suggesting reset password

const LoginPageEngine = props => {
  const pagePath = window.location.pathname;
  const [, tenantId, pageId] = pagePath.split('/');
  const queryParams = queryString.parse(window.location.search);
  const redirectPath = queryParams.redirect;
  const emailFromQuery = queryParams.email;

  // email step hooks
  const [emailAddress, setEmailAddress] = useState(emailFromQuery || '');
  const [emailAddressError, setEmailAddressError] = useState('');
  const emailRef = useRef();

  // password step hooks
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const passwordRef = useRef();

  const [mfa, setMfa] = useState('');
  const [isRememberThisDevice, setIsRememberThisDevice] = useState(false);
  const [isRememberThisDeviceAllowed, setIsRememberThisDeviceAllowed] =
    useState(false);
  const [shortJwt, setShortJwt] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [
    isMultiFactorAuthSetupDialogOpen,
    setIsMultiFactorAuthSetupDialogOpen
  ] = useState(false);
  const [
    multiFactorAuthSetupDialogUserKey,
    setMultiFactorAuthSetupDialogUserKey
  ] = useState('');
  const [
    multiFactorAuthSetupDialogOneTimeCodes,
    setMultiFactorAuthSetupDialogOneTimeCodes
  ] = useState([
    22345678, 12345678, 12345678, 12345678, 12345678, 12345678, 12345678,
    12345678
  ]);
  const mfaRef = useRef();
  const mfaRecoveryRef = useRef();

  // forgot password step hooks
  const [isResetPasswordEmailSent, setIsResetPasswordEmailSent] =
    useState(false);

  // set password step hooks
  const [newPassword, setNewPassword] = useState('');
  const [newPwdError, setNewPwdError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordStrengthMeter, setPasswordStrengthMeter] = useState(-1);
  const newPasswordRef = useRef();

  const { setLicenseType, initFullJWT } = useGlobalVariables();

  // general hooks
  const [step, setStep] = useState(
    pageId === 'set_password'
      ? STEPS.SET_PASSWORD
      : emailFromQuery
      ? STEPS.PASSWORD
      : STEPS.EMAIL
  );
  const [numOfUnsuccessfulTries, setNumOfUnsuccessfulTries] = useState(0);
  const loaderCount = useRef(0);
  const [isLoading, _setIsLoading] = useState(loaderCount.current);

  const setIsLoading = useCallback(state => {
    state
      ? (loaderCount.current = loaderCount.current + 1)
      : (loaderCount.current = loaderCount.current - 1);
    _setIsLoading(loaderCount.current);
  }, []);

  const t = useTranslations();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const history = useHistory();

  useEffect(() => {
    const lastEmailLogin = cookies.get(`${getTenant()}-sso`);
    if (lastEmailLogin) {
      onAccountLookup(lastEmailLogin);
    }
  }, []);

  const checkForBannerPreferences = decoded => {
    const currentUserId = decoded?.userId;
    if (!currentUserId) {
      return new Promise((resolve, reject) => {
        resolve();
      });
    }
    let code = `is-info-msg-hidden-1-${currentUserId}`;
    let query = '[[{"field": "\'code\'", "op": "=", "value": "' + code + '"}]]';
    const banner1Promise = fetchAPI(
      `/configurations/v1/userpreference?order='metadata'->'createdDate' desc&offset=0&limit=1&query=${query}`
    ).then(res => {
      if (res?.configs && res.configs.length && res.configs[0].value) {
        setLSValue('is-info-msg-hidden-1', true);
      }
      return true;
    });
    code = `is-info-msg-hidden-2-${currentUserId}`;
    query = '[[{"field": "\'code\'", "op": "=", "value": "' + code + '"}]]';
    const banner2Promise = fetchAPI(
      `/configurations/v1/userpreference?order='metadata'->'createdDate' desc&offset=0&limit=1&query=${query}`
    ).then(res => {
      if (res?.configs && res.configs.length && res.configs[0].value) {
        setLSValue('is-info-msg-hidden-2', true);
      }
      return true;
    });
    return Promise.all([banner1Promise, banner2Promise]);
  };

  const checkForRememberThisDeviceSetting = () => {
    return new Promise((resolve, reject) => {
      fetchAPI('/mfasettings')
        .then(res => {
          resolve(res?.rememberThisDevice);
        })
        .catch(e => {
          resolve(false);
        });
    });
  };

  const onLoginSuccess = useCallback(
    auth => {
      const jwtToken = auth.replace(/(Bearer: )/, '') || '';
      const decoded = jwt.decode(jwtToken) || {};

      if (!decoded.privileges) {
        decoded.privileges = [];
      }

      if (
        decoded.privileges.length === 1 &&
        decoded.privileges[0] === 'mfa:read'
      ) {
        const rememberThisDevicePromise = checkForRememberThisDeviceSetting();
        rememberThisDevicePromise.then(isAllowed => {
          setIsRememberThisDeviceAllowed(isAllowed);
          setShortJwt(jwtToken);
          setStep('MFA');
          mfaRef.current.focus();
        });

        return;
      }

      setIsLoading(true);
      const jwtPromise = initFullJWT(getTenant(), auth);
      const bannerPromise = checkForBannerPreferences(decoded);

      Promise.all([jwtPromise, bannerPromise])
        .then(responses => {
          const rememberThisDevice = responses[2];

          return getLicense(LICENSE_TYPE.OMNITRACK_LITE_PRODUCT).then(
            response => {
              if (!isEmpty(response)) {
                setLicenseType(response.value);
              }
            }
          );
        })
        .then(license => {
          if (redirectPath) {
            // if we had redirect action in the url and login was successfull - redirect
            history.push(redirectPath);
            return;
          }
          if (decoded.privileges.indexOf('form:write') !== -1) {
            if (
              license === APP_LICENSE.AML &&
              decoded.userProfile.email !== VINCI_SUPPORT_EMAIL
            ) {
              // get form id and push url
              setIsLoading(true);
              FormsService.getCompactForms({ limit: 1 })
                .then(response => {
                  const formId = (response.results[0] || {}).id;
                  history.push(`/responses/${formId}`);
                })
                .finally(() => setIsLoading(false));
            } else {
              history.push('/forms');
            }
          } else {
            history.push('/welcome');
          }
        })
        .finally(() => setIsLoading(false));
    },
    [mfaRef]
  );

  const onEmailFieldChange = useCallback(
    e => {
      if (numOfUnsuccessfulTries > 0) {
        // if they realized they had issue with their user name and not their password, reset their counter
        setNumOfUnsuccessfulTries(0);
      }
      setEmailAddress((e.target.value || '').trim());
      if (emailAddressError) {
        setEmailAddressError('');
      }
    },
    [emailAddressError, numOfUnsuccessfulTries]
  );

  const onPwdFieldChanged = useCallback(
    e => {
      setPassword(e.target.value);
      if (pwdError) {
        setPwdError('');
      }
    },
    [pwdError]
  );

  const onMfaFieldChanged = useCallback(
    e => {
      let newValue = e.target.value;
      newValue = newValue.replace(/[^0-9]/g, '').substring(0, 6);
      setMfa(newValue);
      if (mfaError) {
        setMfaError('');
      }
    },
    [mfaError]
  );

  const isNewPasswordValid = useCallback(() => {
    if (!newPassword) {
      setTimeout(() => {
        setNewPwdError(t('login.error.emptyPwd'));
      }, 10);
      newPasswordRef.current.focus();
      return false;
    }

    const re =
      /(?=.*?[a-zA-Z])(?=.*?[0-9])(?=.*?[#?!@$%^&*~+=)(\]['"\\|/.,<>`;:}{_-]).{10,}$/g;
    const results = re.test(newPassword);

    if (!results) {
      setTimeout(() => {
        setNewPwdError(t('login.error.req'));
      }, 10);
      newPasswordRef.current.focus();
      return false;
    }

    if (passwordStrengthMeter < 1) {
      /*
                    It means that the password is 
                    too guessable: risky password. (guesses < 10^3)
                    or
                    very guessable: protection from throttled online attacks. (guesses < 10^6)
                    */
      setTimeout(() => {
        setNewPwdError(t('login.error.weak'));
      }, 10);
      newPasswordRef.current.focus();
      return false;
    }
    return true;
  }, [newPassword, passwordStrengthMeter]);

  const blockSetPwdRequests = useRef(false);
  const onPasswordSet = useCallback(() => {
    if (!isNewPasswordValid() || blockSetPwdRequests.current) {
      return;
    }

    blockSetPwdRequests.current = true;
    setIsLoading(true);

    const token = queryString.parse(window.location.search).token;

    fetchAPI('/resetpass', {
      method: 'POST',
      body: {
        newPassword: newPassword,
        token
      },
      handleResponse: r => (r && r.headers ? r : '')
    })
      .then(response => {
        const auth = response.headers.get('Authorization');
        const userKey = response.headers.get('user-key');
        const oneTimeCodes = response.headers.get('one-time-codes');
        if (!auth) {
          throw new Error(t('snackbars.general.error'));
        }

        if (userKey) {
          const jwtToken = auth.replace(/(Bearer: )/, '') || '';
          setShortJwt(jwtToken);
          setIsMultiFactorAuthSetupDialogOpen(true);
          setMultiFactorAuthSetupDialogUserKey(userKey);
          // setMultiFactorAuthSetupDialogOneTimeCodes(oneTimeCodes);
          return;
        }
        onLoginSuccess(auth);
      })
      .catch(e => {
        openSnackbar('error', t('snackbars.general.error'));
      })
      .finally(() => {
        blockSetPwdRequests.current = false;
        setIsLoading(false);
      });
  }, [isNewPasswordValid, newPassword, onLoginSuccess]);

  const onSetNewPasswordInputChange = useCallback(
    e => {
      const value = e.target.value;
      setNewPassword(value);
      if (newPwdError) {
        setNewPwdError('');
      }
      /*
        0 # too guessable: risky password. (guesses < 10^3)

        1 # very guessable: protection from throttled online attacks. (guesses < 10^6)
      
        2 # somewhat guessable: protection from unthrottled online attacks. (guesses < 10^8)
      
        3 # safely unguessable: moderate protection from offline slow-hash scenario. (guesses < 10^10)
      
        4 # very unguessable: strong protection from offline slow-hash scenario. (guesses >= 10^10)
        */

      const mapValues4StrengthMeter = {
        0: 0,
        1: 0,
        2: 1,
        3: 1,
        4: 2
      };

      setPasswordStrengthMeter(mapValues4StrengthMeter[zxcvbn(value).score]);
    },
    [newPwdError]
  );

  const validateEmailAddress = useCallback(() => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(emailAddress).toLowerCase());
  }, [emailAddress]);

  const onKeyPressed = useCallback((e, func, onEmpty) => {
    const ENTER_KEY_CODE = 13;
    if (e.keyCode !== ENTER_KEY_CODE) {
      return; // the key that was pressed is not Enter
    }

    if (!e.target.value) {
      onEmpty && onEmpty();
      return;
    }
    func && func();
  }, []);

  const onNextClicked = useCallback(() => {
    // we set onFocus to clear the error, therfore we must make sure the focus event will accure before
    // we set the error

    if (!emailAddress) {
      setTimeout(() => {
        setEmailAddressError(t('login.error.emptyEmail'));
      }, 10);
      emailRef.current.focus();
      return;
    }

    if (!validateEmailAddress()) {
      setTimeout(() => {
        setEmailAddressError(t('login.invalid_email'));
      }, 10);
      emailRef.current.focus();
      return;
    }

    onAccountLookup(emailAddress, () => {
      setStep(STEPS.PASSWORD);
      passwordRef.current.focus();
    });

    // loader
  }, [validateEmailAddress, emailAddress]);

  const onAccountLookup = useCallback((emailAddress, ifNotSSO = () => {}) => {
    if (emailAddress === VINCI_SUPPORT_EMAIL) {
      ifNotSSO();
      return;
    }
    let _404Err = false;
    setIsLoading(true);
    fetchAPI('/accountlookup', {
      method: 'POST',
      body: {
        username: emailAddress
      },

      handleValidationError: e => {
        if (e && e.code === '404') {
          _404Err = true;
        }
        reject(e);
      }
    })
      .then(r => {
        if (r.firstTime) {
          setStep(STEPS.ACTIVATE_ACCOUNT);
          return;
        }

        if (!r.sso) {
          ifNotSSO();
          return;
        }

        const existingCookie = cookies.get(`${getTenant()}-sso`);
        if (existingCookie !== emailAddress) {
          cookies.set(`${getTenant()}-sso`, emailAddress);
        }

        let redirectQuery = '';
        // redirect ot saml
        if (redirectPath) {
          redirectQuery = `&redirect=${redirectPath}`;
        }

        window.location.href = `https://${window.location.host}/api/saml/v1/login/${tenantId}?saml=true${redirectQuery}`;
      })
      .catch(e => {
        _404Err &&
          setTimeout(() => {
            setEmailAddressError(t('login.error.user.not_found'));
          }, 10);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const onLogin = useCallback(() => {
    if (!password) {
      passwordRef.current.focus();
      setPwdError(t('login.error.emptyPwd'));
      return;
    }

    setIsLoading(true);
    fetchAPI('/login', {
      method: 'POST',
      body: {
        userName: emailAddress,
        password: password
      },
      handleResponse: r => (r && r.headers ? r : ''),
      handleServerError: r => {
        setNumOfUnsuccessfulTries(numOfUnsuccessfulTries + 1);

        if (!r) {
          openSnackbar('error', t('snackbars.general.error'));
        }

        let status;
        if (r.status) {
          status = r.status;
        } else {
          const matches = r.toString().match(/(\d+)/);
          if (matches) {
            status = Number(matches[0]);
          }
        }

        if (status < 500) {
          setTimeout(() => {
            setPwdError(t('login.error'));
          }, 10);
          setPassword('');
          passwordRef.current.focus();
        } else {
          openSnackbar('error', 'login.serverError');
        }
      }
    })
      .then(response => {
        const auth = response.headers.get('Authorization');
        const userKey = response.headers.get('user-key');
        if (userKey) {
          const jwtToken = auth.replace(/(Bearer: )/, '') || '';
          setShortJwt(jwtToken);
          setIsMultiFactorAuthSetupDialogOpen(true);
          setMultiFactorAuthSetupDialogUserKey(userKey);
          return;
        }

        onLoginSuccess(auth);
      })
      .finally(() => setIsLoading(false));
  }, [emailAddress, password, numOfUnsuccessfulTries, onLoginSuccess]);

  const onMFALogin = useCallback(() => {
    if (!mfa) {
      mfaRef.current.focus();
      setMfaError(t('login.error.emptyMfa'));
      return;
    }

    setIsLoading(true);

    const body = {
      mfaCode: mfa
    };

    if (isRememberThisDevice) {
      body.rememberThisDevice = true;
    }

    fetchAPI('/login', {
      method: 'POST',
      body,
      headers: {
        Authorization: 'Bearer: ' + shortJwt
      },
      handleResponse: r => (r && r.headers ? r : ''),
      handleServerError: r => {
        setNumOfUnsuccessfulTries(numOfUnsuccessfulTries + 1);
        setMfaError(t('login.error.mfa'));
      }
    })
      .then(response => {
        const auth = response.headers.get('Authorization');

        onLoginSuccess(auth);
      })
      .finally(() => setIsLoading(false));
  }, [mfa, shortJwt, isRememberThisDevice]);

  const onCompleteFirstMFALogin = useCallback(
    mfaCode => {
      if (!mfaCode) {
        return;
      }
      setIsLoading(true);
      fetchAPI('/login', {
        method: 'POST',
        body: {
          mfaCode
        },
        headers: {
          Authorization: 'Bearer: ' + shortJwt
        },
        handleResponse: r => (r && r.headers ? r : ''),
        handleServerError: r => {
          setNumOfUnsuccessfulTries(numOfUnsuccessfulTries + 1);

          openSnackbar('error', 'login.error.mfa');
        }
      })
        .then(response => {
          const auth = response.headers.get('Authorization');
          setIsMultiFactorAuthSetupDialogOpen(false);
          onLoginSuccess(auth);
        })
        .finally(() => setIsLoading(false));
    },
    [onLogin, shortJwt]
  );

  const onResetPasswordClicked = useCallback(() => {
    setIsLoading(true);

    const body = {
      info: [{ userName: emailAddress }]
    };

    if (redirectPath) {
      body.redirect = redirectPath;
    }

    fetchAPI('/passresettoken', {
      method: 'POST',
      body
    })
      .then(() => {
        setIsResetPasswordEmailSent(true);
      })
      .catch(e => {
        log(e);
        openSnackbar('error', t('snackbars.general.error'));
      })
      .finally(() => setIsLoading(false));
  }, [emailAddress]);

  const onBack2EmailScrnClicked = () => {
    setStep(STEPS.EMAIL);
    emailRef.current.focus();
  };

  const onAccountActivate = useCallback(() => {
    setIsLoading(true);

    const body = {
      info: [{ userName: emailAddress }]
    };

    if (redirectPath) {
      body.redirect = redirectPath;
    }

    fetchAPI('/firsttime', {
      method: 'POST',
      body
    })
      .then(() => {
        setIsResetPasswordEmailSent(true);
      })
      .catch(e => {
        log(e);
        openSnackbar('error', t('snackbars.general.error'));
      })
      .finally(() => setIsLoading(false));
  }, [emailAddress]);

  const onUseRecoveryCodeClicked = useCallback(() => {
    setStep('RECOVERY_CODE');
    // mfaRecoveryRef.current.focus();
  }, [mfaRecoveryRef]);

  const onBackToAppCodeClicked = useCallback(() => {
    setStep('MFA');
    mfaRef.current.focus();
  }, [mfaRef]);

  const onToggleRememberThisDevice = useCallback(() => {
    setIsRememberThisDevice(!isRememberThisDevice);
  }, [isRememberThisDevice]);

  const viewProps = {
    emailAddress,
    onEmailFieldChange,
    password,
    setPassword,
    onNextClicked,
    onLogin,
    onMFALogin,
    emailAddressError,
    setEmailAddressError,
    isLoading: !!isLoading,
    step,
    showPassword,
    setShowPassword,
    isResetPasswordEmailSent,
    onResetPasswordClicked,
    mfaRef,
    mfaRecoveryRef,
    passwordRef,
    emailRef,
    onKeyPressed,
    isMultiFactorAuthSetupDialogOpen,
    setIsMultiFactorAuthSetupDialogOpen,
    multiFactorAuthSetupDialogUserKey,
    multiFactorAuthSetupDialogOneTimeCodes,
    isResetPasswordSuggestionDialogOpen:
      numOfUnsuccessfulTries >= MAX_NUM_OF_FAILED_LOGIN_ATTEMPTES,
    setNumOfUnsuccessfulTries,
    mfa,
    setMfa,
    mfaError,
    setMfaError,
    onMfaFieldChanged,
    onCompleteFirstMFALogin,
    pwdError,
    setPwdError,
    onPwdFieldChanged,
    onBack2EmailScrnClicked,
    newPasswordRef,
    newPassword,
    newPwdError,
    setNewPwdError,
    onSetNewPasswordInputChange,
    showNewPassword,
    setShowNewPassword,
    passwordStrengthMeter,
    onPasswordSet,
    onAccountActivate,
    onUseRecoveryCodeClicked,
    onBackToAppCodeClicked,
    isRememberThisDevice,
    isRememberThisDeviceAllowed,
    onToggleRememberThisDevice
  };

  return <LoginPageView {...viewProps} />;
};

export default LoginPageEngine;
